import { Bold, Italic, Link2, List, ListOrdered, Underline, Strikethrough, Eraser } from 'lucide-react';
import { type Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { EMOJI } from '../../lib/emoji';
import { useDismissOnOutside } from '../../lib/useDismiss';

/**
 * The styled reply box. A contenteditable rather than a library: the console has
 * no editor dependency and the four commands mail actually needs (emphasis, lists,
 * links) are the ones execCommand still does correctly everywhere. It is
 * deprecated but not replaced — the alternative is hand-rolling selection surgery.
 *
 * The html leaves here untrusted and is sanitized server-side before it is stored,
 * rendered to other agents, or mailed.
 */
const TOOLS = [
  { command: 'bold', title: 'Bold (⌘B)', icon: Bold },
  { command: 'italic', title: 'Italic (⌘I)', icon: Italic },
  { command: 'underline', title: 'Underline (⌘U)', icon: Underline },
  { command: 'strikeThrough', title: 'Strikethrough', icon: Strikethrough },
  { command: 'insertUnorderedList', title: 'Bulleted list', icon: List },
  { command: 'insertOrderedList', title: 'Numbered list', icon: ListOrdered },
  { command: 'createLink', title: 'Insert link', icon: Link2 },
  { command: 'removeFormat', title: 'Clear formatting', icon: Eraser },
] as const;

export interface RichTextValue {
  html: string;
  text: string;
}

/** What a parent can drive from outside — inserting at the caret needs the dom. */
export interface RichTextHandle {
  /** Insert plain text where the caret is, appending if the box was never focused. */
  insert: (text: string) => void;
  /**
   * The same, for markup: a saved block goes in as html rather than as the
   * characters that spell it. The caller is responsible for what it passes —
   * the server sanitizes this box's output either way.
   */
  insertHtml: (html: string) => void;
}

export function RichText({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  ref,
}: {
  value: RichTextValue;
  onChange: (value: RichTextValue) => void;
  onSubmit?: () => void;
  placeholder: string;
  disabled?: boolean;
  ref?: Ref<RichTextHandle>;
}) {
  const editor = useRef<HTMLDivElement>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiRef = useDismissOnOutside<HTMLDivElement>(emojiOpen, () => setEmojiOpen(false));
  const [active, setActive] = useState<Record<string, boolean>>({});

  // The editor owns its dom while focused — writing value back on every keystroke
  // would reset the caret to the start. Sync only when the two have diverged,
  // which is how a parent reset (send, template insert) lands.
  useEffect(() => {
    const node = editor.current;
    if (node && node.innerHTML !== value.html) node.innerHTML = value.html;
  }, [value.html]);

  const emit = useCallback(() => {
    const node = editor.current;
    if (!node) return;
    onChange({ html: node.innerHTML, text: node.innerText });
  }, [onChange]);

  const command = (name: string, arg?: string) => {
    editor.current?.focus();
    document.execCommand(name, false, arg);
    refreshActive();
    emit();
  };

  const refreshActive = () => {
    const state: Record<string, boolean> = {};
    for (const name of ['bold', 'italic', 'underline', 'strikeThrough']) {
      try {
        state[name] = document.queryCommandState(name);
      } catch {
        state[name] = false;
      }
    }
    setActive(state);
  };

  const addLink = () => {
    const url = window.prompt('Link URL');
    if (!url) return;
    // A bare domain would be resolved against the console's own origin.
    command('createLink', /^https?:\/\/|^mailto:/i.test(url) ? url : `https://${url}`);
  };

  const insert = (text: string) => {
    editor.current?.focus();
    document.execCommand('insertText', false, text);
    emit();
  };

  // focus() alone puts the caret at the START of a box the user has not typed
  // in yet, so an inserted tag would land before the copy instead of after it.
  const atCaret = (write: () => void) => {
    const node = editor.current;
    if (!node) return;
    if (!node.contains(document.getSelection()?.anchorNode ?? null)) {
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    write();
  };

  /**
   * Step the caret out to the editor's own children before markup goes in.
   *
   * insertHTML splices at exactly the caret, and a caret inside a <p> cannot
   * hold another <p> — the browser unwraps the incoming block into a <span>,
   * which flattens a signature or a letterhead into the sentence being written.
   * A block lands after the paragraph the agent is in, which is where they
   * meant to put it anyway.
   */
  const caretToBlockEdge = (node: HTMLDivElement) => {
    const selection = document.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if (!range || !node.contains(range.endContainer)) return;
    // The caret is already between the editor's own children — an empty box, or a
    // caret parked between two blocks. Nothing to step out of, and climbing from
    // here would walk off the top of the document.
    if (range.endContainer === node) return;
    let outermost: Node = range.endContainer;
    while (outermost.parentNode && outermost.parentNode !== node) outermost = outermost.parentNode;
    if (outermost.parentNode !== node) return;
    const after = document.createRange();
    after.setStartAfter(outermost);
    after.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(after);
  };

  useImperativeHandle(ref, () => ({
    insert: (text: string) => atCaret(() => insert(text)),
    insertHtml: (html: string) =>
      atCaret(() => {
        const node = editor.current;
        if (node) caretToBlockEdge(node);
        // insertHTML merges the fragment's FIRST block into the block holding
        // the caret, whatever the caret is sitting in — so a signature arrives
        // with its opening line torn off and glued to the last paragraph. A
        // leading <br> takes that merge instead, and doubles as the blank line
        // you want between a message and what is appended to it. Not on an
        // empty box, where it would only indent the first line.
        const spacer = node && node.innerHTML.trim() ? '<br>' : '';
        command('insertHTML', `${spacer}${html}`);
      }),
  }));

  return (
    <div className="rt">
      <div className="rt-tools">
        {TOOLS.map((tool) => (
          <button
            key={tool.command}
            type="button"
            className={`rt-tool${active[tool.command] ? ' on' : ''}`}
            title={tool.title}
            disabled={disabled}
            // The editor loses its selection to a focus change, so the press must
            // not move focus in the first place.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => (tool.command === 'createLink' ? addLink() : command(tool.command))}
          >
            <tool.icon size={13} />
          </button>
        ))}
        <div className="rt-emoji-wrap">
          <button
            type="button"
            className={`rt-tool${emojiOpen ? ' on' : ''}`}
            title="Emoji"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setEmojiOpen((open) => !open)}
          >
            <span className="rt-emoji-glyph">🙂</span>
          </button>
          {emojiOpen && (
            <div className="rt-emoji" ref={emojiRef}>
              {EMOJI.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    insert(emoji);
                    setEmojiOpen(false);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div
        ref={editor}
        className="rt-input"
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            onSubmit?.();
          }
        }}
      />
    </div>
  );
}
